import * as tx from '@staiter/i18n';
/**
 * FM-S-multi-4-12 — /admin/holdings/$holdingId detail.
 * Tabs: «Компании», «{tx.ui_polzovateli()}», «{tx.ui_nastroyki()}».
 */
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Lock, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { emitToast } from '~/components/feedback';
import { Button, ConfirmDialog, Input, Select } from '~/components/primitives';
import {
  useArchiveHolding,
  useHolding,
  useHoldingUsers,
  useRemoveHoldingMembership,
  useUpdateEntity,
  useUpdateHolding,
  useUpdateHoldingMembership,
} from '~/lib/api/tenancy';
import { getRoleLabel } from '~/lib/rbac';
import { useIsSuperAdmin } from '~/lib/rbac/use-tenancy-rbac';

const HOLDING_ROLE_OPTIONS = [
  { value: 'holding_admin', label: getRoleLabel('holding_admin') },
  { value: 'holding_viewer', label: getRoleLabel('holding_viewer') },
];

export const Route = createFileRoute('/_auth/admin/holdings_/$holdingId')({
  staticData: { minMainWidth: 960 },
  component: HoldingDetail,
});

type Tab = 'entities' | 'users' | 'settings';

function HoldingDetail(): JSX.Element {
  const { holdingId } = Route.useParams();
  const isSuper = useIsSuperAdmin();
  const [tab, setTab] = useState<Tab>('entities');
  const detail = useHolding(holdingId);
  const usersQ = useHoldingUsers(holdingId);

  if (!isSuper) {
    return (
      <div style={{ padding: '24px 0' }}>
        <p style={{ color: 'var(--text-muted)' }}>{tx.ui_dostupno_tolko_super_admin()}</p>
      </div>
    );
  }

  if (detail.isLoading) return <p>{tx.ui_zagruzka()}</p>;
  if (!detail.data) return <p>{tx.ui_holding_ne_nayden()}</p>;

  const { holding, entities } = detail.data;

  return (
    <div>
      <PageHeader title={holding.name} description={`Статус: ${holding.status}`} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button
          variant={tab === 'entities' ? 'primary' : 'ghost'}
          onClick={() => setTab('entities')}
        >
          Компании ({entities.length})
        </Button>
        <Button variant={tab === 'users' ? 'primary' : 'ghost'} onClick={() => setTab('users')}>
          {tx.ui_polzovateli()}
        </Button>
        <Button
          variant={tab === 'settings' ? 'primary' : 'ghost'}
          onClick={() => setTab('settings')}
        >
          {tx.ui_nastroyki()}
        </Button>
      </div>

      {tab === 'entities' && (
        <Table
          ariaLabel="Компании холдинга"
          data={entities}
          rowKey={(e) => e.id}
          columns={[
            { id: 'name', header: 'Название', accessor: (e) => e.name },
            { id: 'bin', header: 'БИН', accessor: (e) => e.bin ?? '—' },
            { id: 'currency', header: 'Валюта', accessor: (e) => e.default_currency },
            { id: 'status', header: 'Статус', accessor: (e) => e.status },
            {
              id: 'actions',
              header: '',
              accessor: (e) => (
                <HoldingCompanyActions holdingId={holdingId} entityId={e.id} name={e.name} />
              ),
            },
          ]}
        />
      )}

      {tab === 'users' && (
        <Table
          ariaLabel="Пользователи холдинга"
          data={(usersQ.data?.items ?? []) as Array<Record<string, unknown>>}
          rowKey={(u) => u.user_id as string}
          columns={[
            { id: 'email', header: 'Email', accessor: (u) => u.email as string },
            { id: 'name', header: 'Имя', accessor: (u) => (u.full_name as string) ?? '—' },
            {
              id: 'source',
              header: 'Источник',
              accessor: (u) => <HoldingSourceChip source={u.source as string} />,
            },
            {
              id: 'role',
              header: 'Роль',
              accessor: (u) => (
                <HoldingRoleCell
                  holdingId={holdingId}
                  userId={u.user_id as string}
                  effectiveRole={u.effective_role as string}
                  source={u.source as string}
                />
              ),
            },
            {
              id: 'actions',
              header: '',
              accessor: (u) => (
                <HoldingRemoveCell
                  holdingId={holdingId}
                  userId={u.user_id as string}
                  source={u.source as string}
                />
              ),
            },
          ]}
        />
      )}

      {tab === 'settings' && <HoldingSettingsForm holdingId={holdingId} />}
    </div>
  );
}

/** В холдинг-виде: 'holding' = собственный доступ (редактируем здесь), 'direct' = через компанию. */
function HoldingSourceChip({ source }: { source: string }): JSX.Element {
  const own = source === 'holding';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--radius-sm, 6px)',
        fontSize: '0.75rem',
        fontWeight: 500,
        background: own
          ? 'var(--accent-bg-15, rgba(64,120,255,0.15))'
          : 'var(--surface-sunken, rgba(127,127,127,0.12))',
        color: own ? 'var(--accent-strong, inherit)' : 'var(--text-secondary, inherit)',
      }}
    >
      {own ? 'Холдинг' : 'Через компанию'}
    </span>
  );
}

/** Read-only роль (derived-доступ) — muted-бейдж с замком: «так задумано», не «не догрузилось». */
function ReadOnlyRoleBadge({ role }: { role: string }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 'var(--radius-pill, 999px)',
        fontSize: '0.8rem',
        color: 'var(--text-muted)',
        background: 'var(--surface-sunken, rgba(127,127,127,0.10))',
      }}
    >
      <Lock size={12} aria-hidden /> {getRoleLabel(role)}
    </span>
  );
}

/** Danger-кнопка удаления в строке таблицы. */
function RemoveButton({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      style={{ color: 'var(--danger)' }}
      leadingIcon={<Trash2 size={14} />}
    >
      {label}
    </Button>
  );
}

/** Убрать компанию из холдинга = снять holding_id (super-only, BE enforce). */
function HoldingCompanyActions({
  holdingId,
  entityId,
  name,
}: {
  holdingId: string;
  entityId: string;
  name: string;
}): JSX.Element {
  const qc = useQueryClient();
  const m = useUpdateEntity(entityId);
  const [confirm, setConfirm] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
      <Link to="/admin/entities/$entityId" params={{ entityId }}>
        <Button variant="ghost" size="sm">
          {tx.ui_otkryt()}
        </Button>
      </Link>
      <RemoveButton label="Убрать из холдинга" onClick={() => setConfirm(true)} />
      <ConfirmDialog
        open={confirm}
        variant="danger"
        title="Убрать компанию из холдинга?"
        description={`«${name}» станет standalone. Пользователи, имевшие доступ через холдинг, потеряют его к этой компании.`}
        confirmLabel="Убрать"
        onConfirm={async () => {
          try {
            await m.mutateAsync({ holding_id: null });
            emitToast({ variant: 'success', title: 'Убрано из холдинга' });
            await qc.invalidateQueries({ queryKey: ['admin', 'holdings', holdingId] });
            await qc.invalidateQueries({ queryKey: ['admin', 'entities'] });
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось убрать' });
          }
          setConfirm(false);
        }}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
}

/**
 * Роль пользователя в холдинге: Select для `source=holding`, иначе read-only lock-бейдж
 * (доступ через компанию). Raw holding-роль выводим из effective (BE маппит holding_admin↔company_admin).
 */
function HoldingRoleCell({
  holdingId,
  userId,
  effectiveRole,
  source,
}: {
  holdingId: string;
  userId: string;
  effectiveRole: string;
  source: string;
}): JSX.Element {
  const qc = useQueryClient();
  const update = useUpdateHoldingMembership(userId);

  if (source !== 'holding') return <ReadOnlyRoleBadge role={effectiveRole} />;

  const rawRole = effectiveRole === 'company_admin' ? 'holding_admin' : 'holding_viewer';

  return (
    <div style={{ maxWidth: 210 }}>
      <Select
        label=""
        size="sm"
        value={rawRole}
        disabled={update.isPending}
        onChange={async (e) => {
          try {
            await update.mutateAsync({ holdingId, input: { role: e.target.value } });
            await qc.invalidateQueries({ queryKey: ['admin', 'holdings', holdingId, 'users'] });
            emitToast({ variant: 'success', title: 'Роль изменена' });
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось изменить роль' });
          }
        }}
        options={HOLDING_ROLE_OPTIONS}
      />
    </div>
  );
}

/** «Убрать» из холдинга — только для `source=holding`. */
function HoldingRemoveCell({
  holdingId,
  userId,
  source,
}: {
  holdingId: string;
  userId: string;
  source: string;
}): JSX.Element | null {
  const qc = useQueryClient();
  const remove = useRemoveHoldingMembership(userId);
  const [confirmRemove, setConfirmRemove] = useState(false);
  if (source !== 'holding') return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <RemoveButton label="Убрать" onClick={() => setConfirmRemove(true)} />
      <ConfirmDialog
        open={confirmRemove}
        variant="danger"
        title="Убрать из холдинга?"
        description="Пользователь потеряет доступ ко всем компаниям холдинга, унаследованный через это членство."
        confirmLabel="Убрать"
        onConfirm={async () => {
          try {
            await remove.mutateAsync({ holdingId });
            await qc.invalidateQueries({ queryKey: ['admin', 'holdings', holdingId, 'users'] });
            emitToast({ variant: 'success', title: 'Удалено' });
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось удалить' });
          }
          setConfirmRemove(false);
        }}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}

function HoldingSettingsForm({ holdingId }: { holdingId: string }): JSX.Element {
  const detail = useHolding(holdingId);
  const qc = useQueryClient();
  const update = useUpdateHolding(holdingId);
  const archive = useArchiveHolding();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [status, setStatus] = useState<'active' | 'archived'>('active');

  useEffect(() => {
    if (detail.data) {
      setName(detail.data.holding.name);
      setStatus(detail.data.holding.status as 'active' | 'archived');
    }
  }, [detail.data]);

  if (!detail.data) return <></>;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await update.mutateAsync({ name, status });
          emitToast({ variant: 'success', title: 'Сохранено' });
          await qc.invalidateQueries({ queryKey: ['admin', 'holdings'] });
        } catch {
          emitToast({ variant: 'error', title: 'Не удалось сохранить' });
        }
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 500 }}
    >
      <Input
        label="Название холдинга"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <Select
        label="Статус"
        value={status}
        onChange={(e) => setStatus(e.target.value as 'active' | 'archived')}
        options={[
          { value: 'active', label: 'Активен' },
          { value: 'archived', label: 'В архиве' },
        ]}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button type="submit" disabled={update.isPending}>
          {tx.ui_sohranit()}
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={archive.isPending}
          onClick={async () => {
            if (
              !confirm(
                'Удалить (архивировать) холдинг? Если есть активные компании — будет soft delete.',
              )
            )
              return;
            try {
              await archive.mutateAsync(holdingId);
              emitToast({ variant: 'success', title: 'Холдинг удалён' });
              await qc.invalidateQueries({ queryKey: ['admin', 'holdings'] });
              navigate({ to: '/admin/holdings' });
            } catch {
              emitToast({ variant: 'error', title: 'Не удалось удалить' });
            }
          }}
        >
          {tx.ui_udalit_holding()}
        </Button>
      </div>
    </form>
  );
}
